import { useCallback, useEffect, useRef, useState } from 'react';
import type { PendingVisit } from '@inertiajs/core';
import { router } from '@inertiajs/react';
import { toast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';

import { AI_TOAST_MESSAGES } from '@/components/shared/ai-menu-constants';
import { buildCompletionOptions } from '@/lib/assistant/build-completion-options';
import { getStreamErrorMessage } from '@/lib/mastra/stream';
import { useAiOperationsStore } from '@/stores/ai-operations-store';
import { pauseAutosave, resumeAutosave } from '@/stores/page-editor-middleware';
import { TaskType } from '@/types/assistant';
import type { SiteBrandContext } from '@/types/page';
import { useMastraAgent } from './use-mastra-agent';
import { useMastraClient } from './use-mastra-client';
import { STREAMING_IN_PROGRESS_ERROR } from './use-mastra-stream-runner';

const COMPLETION_SCOPE = 'content.completion';

interface StreamingState<T = Record<string, unknown>> {
  isOpen: boolean;
  title: string;
  task: TaskType;
  metadata?: T;
}

interface UseStreamingCompletionOptions extends SiteBrandContext {
  userId: string;
  accountId: string | null;
  onComplete?: () => void;
  validateContent?: (content: string) => boolean;
}

export interface NavigationDialogState {
  isOpen: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Hook for streaming AI completion operations with state management.
 *
 * Features:
 * - Streaming completions with real-time content updates
 * - Navigation blocking during active operations
 * - Retry and cancellation support
 * - Error handling and state management
 *
 */
export function useStreamingCompletion<T = Record<string, unknown>>({
  userId,
  accountId,
  onComplete,
  validateContent,
  preferences,
  firmContext,
}: UseStreamingCompletionOptions) {
  const [streamingState, setStreamingState] = useState<StreamingState<T>>(() => ({
    isOpen: false,
    title: '',
    task: TaskType.REPHRASE,
  }));

  const [navigationDialog, setNavigationDialog] = useState<NavigationDialogState>({
    isOpen: false,
    onConfirm: () => {},
    onCancel: () => {},
  });

  const [streamedContent, setStreamedContent] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [currentThreadId, setCurrentThreadId] = useState('');
  const mastraClient = useMastraClient({
    threadId: currentThreadId || undefined,
    scopes: [COMPLETION_SCOPE],
  });
  const {
    isStreaming,
    sendMessage: streamCompletion,
    stop: stopCompletionStream,
  } = useMastraAgent({
    agentId: 'completion',
    mastraClient,
  });
  const allowNavigationRef = useRef(false);
  const setIsAIStreamingOperation = useAiOperationsStore((state) => state.setIsStreaming);

  const resetState = useCallback(() => {
    setStreamingState({
      isOpen: false,
      title: '',
      task: TaskType.REPHRASE,
      metadata: undefined,
    });

    setStreamedContent('');
    setError(null);
    setIsAIStreamingOperation(false);
    setCurrentThreadId('');
  }, [setIsAIStreamingOperation]);

  // Intercept navigation while the AI menu is open
  useEffect(() => {
    if (!streamingState.isOpen) return;

    const handleBeforeNavigate = (event: CustomEvent<{ visit: PendingVisit }>) => {
      // Allow navigation if user confirmed
      if (allowNavigationRef.current) {
        allowNavigationRef.current = false;
        return true;
      }

      // If streaming is in progress OR has finished with pending content, prevent navigation and show blocking dialog
      if (isStreaming || (streamedContent && !error)) {
        event.preventDefault();

        const visit = event.detail.visit;

        setNavigationDialog({
          isOpen: true,
          onConfirm: () => {
            // Clean up state
            stopCompletionStream();
            resetState();

            // Close dialog
            setNavigationDialog({
              isOpen: false,
              onConfirm: () => {},
              onCancel: () => {},
            });

            // Set flag to allow navigation
            allowNavigationRef.current = true;

            router.visit(visit.url);
          },
          onCancel: () => {
            setNavigationDialog({
              isOpen: false,
              onConfirm: () => {},
              onCancel: () => {},
            });
          },
        });

        return false;
      }
    };

    const removeListener = router.on('before', handleBeforeNavigate);

    return () => {
      removeListener();
    };
  }, [
    streamingState.isOpen,
    isStreaming,
    streamedContent,
    error,
    stopCompletionStream,
    resetState,
  ]);

  /**
   * Streaming executor.
   *
   * Responsibilities:
   * - Manages streaming lifecycle state (streamedContent, error).
   * - Coordinates thread id handling for retries.
   *
   * Error contract:
   * - Throws Error('NO_CONTENT') when content is empty/whitespace.
   * - Throws Error('STREAMING_IN_PROGRESS') when already streaming.
   * - Throws AbortError when the active stream is cancelled.
   * - Throws any other unexpected error from the streaming pipeline.
   *
   * Callers MUST wrap in try/catch and:
   * - Map sentinel errors (e.g. 'NO_CONTENT') to appropriate UX.
   * - Avoid swallowing AbortError if they need special handling.
   */
  const executeStreaming = useCallback(
    async ({ task, content }: { task: TaskType; content: string }) => {
      if (!content.trim()) {
        throw new Error('NO_CONTENT');
      }

      // Always ensure thread ID is set for new operations
      // Reuse existing thread ID for retries, create new one for new operations
      const threadId = currentThreadId || uuidv4();
      if (!currentThreadId) {
        setCurrentThreadId(threadId);
      }

      pauseAutosave();
      setIsAIStreamingOperation(true);
      setError(null);
      setStreamedContent('');

      try {
        const options = buildCompletionOptions({
          userId,
          accountId,
          task,
          threadId,
          preferences,
          firmContext,
        });

        let fullContent = '';
        await streamCompletion(content, {
          concurrency: 'reject',
          streamOptions: options,
          onChunk: (chunk) => {
            switch (chunk.type) {
              case 'text-delta': {
                const delta = typeof chunk.payload?.text === 'string' ? chunk.payload.text : '';
                fullContent += delta;
                setStreamedContent(fullContent);
                break;
              }
              case 'error': {
                throw new Error(getStreamErrorMessage(chunk.payload?.error));
              }
            }
          },
        });

        // Keep global operation flag true until user takes action (Replace/Insert/Discard)

        if (!fullContent.trim()) {
          setError('No content was generated. Please try again.');
        }
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          setIsAIStreamingOperation(false);
          return;
        }

        console.error('Streaming completion error:', err);
        const errorMessage = err instanceof Error ? err.message : 'Failed to generate content';

        setError(errorMessage);
        setIsAIStreamingOperation(false);
        throw err;
      } finally {
        resumeAutosave();
      }
    },
    [
      userId,
      accountId,
      currentThreadId,
      setIsAIStreamingOperation,
      preferences,
      firmContext,
      streamCompletion,
    ],
  );

  const startOperation = useCallback(
    async (params: { task: TaskType; content: string; title: string; metadata?: T }) => {
      if (validateContent && !validateContent(params.content)) {
        if (onComplete) {
          onComplete();
        }
        return false;
      }

      try {
        setStreamingState({
          isOpen: true,
          title: params.title,
          task: params.task,
          metadata: params.metadata,
        });

        await executeStreaming({
          task: params.task,
          content: params.content,
        });

        return true;
      } catch (error) {
        const message = error instanceof Error ? error.message : '';

        // Handle specific error cases
        if (message === STREAMING_IN_PROGRESS_ERROR) {
          // Silently ignore concurrent operation attempts
          return false;
        }

        const toastMessage =
          message === 'NO_CONTENT'
            ? AI_TOAST_MESSAGES.NO_CONTENT
            : AI_TOAST_MESSAGES.GENERATION_FAILED;

        toast.error(toastMessage);

        console.error('AI operation error:', error);
        resetState();
        if (onComplete) {
          onComplete();
        }
        return false;
      }
    },
    [validateContent, onComplete, executeStreaming, resetState],
  );

  const retry = useCallback(
    async (content: string) => {
      try {
        setStreamedContent('');
        setError(null);

        await executeStreaming({
          task: streamingState.task,
          content,
        });
      } catch (error) {
        const message = error instanceof Error ? error.message : '';

        const toastMessage =
          message === 'NO_CONTENT' ? AI_TOAST_MESSAGES.NO_CONTENT : AI_TOAST_MESSAGES.RETRY_FAILED;

        toast.error(toastMessage);
        console.error('Retry error:', error);
      }
    },
    [streamingState.task, executeStreaming],
  );

  const cancel = useCallback(() => {
    stopCompletionStream();
    resetState();
    toast.info(AI_TOAST_MESSAGES.OPERATION_CANCELLED);
    if (onComplete) {
      onComplete();
    }
  }, [stopCompletionStream, resetState, onComplete]);

  const discard = useCallback(() => {
    resetState();
    if (onComplete) {
      onComplete();
    }
  }, [resetState, onComplete]);

  return {
    streamingState,
    streamedContent,
    isStreaming,
    error,
    startOperation,
    retry,
    cancel,
    discard,
    resetState,
    navigationDialog,
  };
}
